import { useMemo, useState } from "react"
import { formatPercent } from "atlas_forecasting_app/lib/format"
import { clamp01 } from "atlas_forecasting_app/lib/math"
import { useContainerWidth } from "atlas_forecasting_app/hooks/use_container_width"
import { ChartTooltip } from "atlas_forecasting_app/components/charts/chart_tooltip"
import { statsFromDiscrete } from "atlas_forecasting_app/forecasting/forecast_utils"

function cmfTitleForBucket(bucket) {
  if (!bucket) return ""
  if (bucket.kind === "below") return bucket.label
  if (bucket.kind === "above") return "Any"
  return `≤${bucket.label}`
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function discreteAnnotationLayout(stats, xForIndex, chartLeft, chartRight, innerH) {
  if (!stats) return null

  const intervalCenterX = (xForIndex(stats.p10Index) + xForIndex(stats.p90Index)) / 2
  const intervalLabelWidth = 94
  const intervalLabelX = clamp(intervalCenterX, chartLeft + intervalLabelWidth / 2, chartRight - intervalLabelWidth / 2)
  const medianLabelWidth = 64
  const medianX = xForIndex(stats.p50Index)
  const medianLabelX = clamp(medianX, chartLeft + medianLabelWidth / 2, chartRight - medianLabelWidth / 2)

  return {
    intervalBracketY: Math.max(118, innerH - 26),
    intervalLabelX,
    intervalLabelY: Math.max(106, innerH - 38),
    intervalLabelTop: Math.max(106, innerH - 38) - 11,
    intervalLabelBottom: Math.max(106, innerH - 38) + 7,
    medianLabelX,
    medianLabelY: 12,
    medianCutsIntervalLabel: medianX >= intervalLabelX - intervalLabelWidth / 2 && medianX <= intervalLabelX + intervalLabelWidth / 2,
  }
}

export function DiscreteHistogramChart({ buckets, mode = "pmf", height }) {
  const [ref, width] = useContainerWidth()
  const [hoverIdx, setHoverIdx] = useState(null)
  const [tooltip, setTooltip] = useState(null)
  const gradId = useMemo(() => `af_hist_${Math.random().toString(16).slice(2)}`, [])

  const margin = { top: 16, right: 16, bottom: 34, left: 52 }
  const computedHeight = height || 240
  const innerW = Math.max(10, (width || 0) - margin.left - margin.right)
  const innerH = Math.max(10, computedHeight - margin.top - margin.bottom)
  const probs = useMemo(() => (buckets || []).map((bucket) => clamp01(Number(bucket?.probability)) ?? 0), [buckets])
  const barW = innerW / Math.max(1, buckets?.length || 0)

  const stats = useMemo(() => statsFromDiscrete(buckets), [buckets])

  const cdf = useMemo(() => {
    let total = 0
    return probs.map((probability) => {
      total = clamp01(total + probability) ?? 0
      return total
    })
  }, [probs])

  const activeSeries = mode === "cmf" ? cdf : probs
  const yMax = mode === "cmf" ? 1 : Math.max(0.01, ...probs)
  const yScaleMax = mode === "cmf" ? 1 : Math.min(1, yMax * 1.15)

  const xForIndex = (idx) => margin.left + idx * barW + barW / 2
  const yForValue = (value) => margin.top + innerH * (1 - value / yScaleMax)
  const annotationLayout = useMemo(
    () => discreteAnnotationLayout(stats, xForIndex, margin.left, margin.left + innerW, innerH),
    [stats, innerW, innerH, barW],
  )

  const pathD = useMemo(() => {
    if (mode !== "cmf" || activeSeries.length === 0) return ""
    return activeSeries
      .map((value, idx) => {
        const x = xForIndex(idx)
        const y = yForValue(value)
        return `${idx === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`
      })
      .join(" ")
  }, [mode, activeSeries, barW, innerH, yScaleMax])

  const areaD = useMemo(() => {
    if (mode !== "cmf" || activeSeries.length === 0) return ""
    const x0 = xForIndex(0)
    const xN = xForIndex(activeSeries.length - 1)
    const yBottom = margin.top + innerH
    return `${pathD} L ${xN.toFixed(2)} ${yBottom.toFixed(2)} L ${x0.toFixed(2)} ${yBottom.toFixed(2)} Z`
  }, [mode, activeSeries, pathD, barW, innerH])

  const onMove = (event) => {
    if (!width) return
    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    const idx = Math.floor((x - margin.left) / barW)
    if (idx >= 0 && idx < probs.length && y >= margin.top - 6 && y <= margin.top + innerH + 6) {
      const bucket = buckets[idx]
      if (!bucket) return
      setHoverIdx(idx)
      const probability = activeSeries[idx]
      const title = mode === "cmf" ? cmfTitleForBucket(bucket) : `Outcome ${bucket.label}`
      const body = mode === "cmf" ? `CMF: ${formatPercent(probability, 2)}` : `Probability: ${formatPercent(probability, 2)}`
      setTooltip({ x, y, title, body })
    } else {
      setHoverIdx(null)
      setTooltip(null)
    }
  }

  const tickCount = Math.min(6, probs.length)
  const xTicks = Array.from({ length: tickCount }, (_, i) => Math.round((i * (probs.length - 1)) / Math.max(1, tickCount - 1)))

  return (
    <div ref={ref} className="af-chart" style={{ height: `${computedHeight}px` }}>
      {width > 0 ? (
        <svg
          width={width}
          height={computedHeight}
          onMouseMove={onMove}
          onMouseLeave={() => {
            setHoverIdx(null)
            setTooltip(null)
          }}
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={mode === "cmf" ? "rgba(34, 139, 230, 0.20)" : "rgba(34, 139, 230, 0.85)"} />
              <stop offset="85%" stopColor={mode === "cmf" ? "rgba(34, 139, 230, 0.03)" : "rgba(34, 139, 230, 0.08)"} />
            </linearGradient>
          </defs>

          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const value = yScaleMax * t
            const y = yForValue(value)
            return (
              <g key={t}>
                <line x1={margin.left} y1={y} x2={margin.left + innerW} y2={y} className={t === 0 ? "af-chart-grid" : "af-chart-grid-soft"} />
                <text x={margin.left - 8} y={y + 4} textAnchor="end" className="af-chart-axis-muted">
                  {formatPercent(value, mode === "cmf" ? 0 : 1)}
                </text>
              </g>
            )
          })}

          {stats ? (
            <g>
              <rect x={margin.left + stats.p10Index * barW} y={margin.top} width={Math.max(0, (stats.p90Index - stats.p10Index + 1) * barW)} height={innerH} fill="rgba(18, 184, 134, 0.08)" />
              <line x1={xForIndex(stats.p10Index)} y1={margin.top} x2={xForIndex(stats.p10Index)} y2={margin.top + innerH} stroke="rgba(134, 142, 150, 0.55)" strokeDasharray="4 4" />
              <line x1={xForIndex(stats.p90Index)} y1={margin.top} x2={xForIndex(stats.p90Index)} y2={margin.top + innerH} stroke="rgba(134, 142, 150, 0.55)" strokeDasharray="4 4" />
              <line x1={xForIndex(stats.p10Index)} y1={margin.top + annotationLayout.intervalBracketY} x2={xForIndex(stats.p90Index)} y2={margin.top + annotationLayout.intervalBracketY} stroke="rgba(18, 184, 134, 0.70)" strokeWidth="1.5" />
              <line x1={xForIndex(stats.p10Index)} y1={margin.top + annotationLayout.intervalBracketY - 5} x2={xForIndex(stats.p10Index)} y2={margin.top + annotationLayout.intervalBracketY + 5} stroke="rgba(18, 184, 134, 0.70)" strokeWidth="1.5" />
              <line x1={xForIndex(stats.p90Index)} y1={margin.top + annotationLayout.intervalBracketY - 5} x2={xForIndex(stats.p90Index)} y2={margin.top + annotationLayout.intervalBracketY + 5} stroke="rgba(18, 184, 134, 0.70)" strokeWidth="1.5" />
              <rect x={annotationLayout.intervalLabelX - 47} y={margin.top + annotationLayout.intervalLabelY - 11} width="94" height="18" rx="9" fill="rgba(248, 249, 250, 0.94)" stroke="rgba(18, 184, 134, 0.35)" />
              <text
                x={annotationLayout.intervalLabelX}
                y={margin.top + annotationLayout.intervalLabelY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="af-chart-axis"
                fill="rgba(8, 127, 91, 0.96)"
                style={{ fontWeight: 600 }}
              >
                80% interval
              </text>
            </g>
          ) : null}

          {hoverIdx != null ? <line x1={xForIndex(hoverIdx)} y1={margin.top} x2={xForIndex(hoverIdx)} y2={margin.top + innerH} className="af-chart-grid" /> : null}

          {stats ? (
            <g>
              {annotationLayout.medianCutsIntervalLabel ? (
                <g>
                  <line x1={xForIndex(stats.p50Index)} y1={margin.top} x2={xForIndex(stats.p50Index)} y2={margin.top + annotationLayout.intervalLabelTop - 3} stroke="rgba(18, 184, 134, 0.65)" strokeWidth="2" />
                  <line x1={xForIndex(stats.p50Index)} y1={margin.top + annotationLayout.intervalLabelBottom + 3} x2={xForIndex(stats.p50Index)} y2={margin.top + innerH} stroke="rgba(18, 184, 134, 0.65)" strokeWidth="2" />
                </g>
              ) : (
                <line x1={xForIndex(stats.p50Index)} y1={margin.top} x2={xForIndex(stats.p50Index)} y2={margin.top + innerH} stroke="rgba(18, 184, 134, 0.65)" strokeWidth="2" />
              )}
              <rect x={annotationLayout.medianLabelX - 32} y={margin.top + annotationLayout.medianLabelY - 11} width="64" height="18" rx="9" fill="rgba(235, 251, 238, 0.96)" stroke="rgba(18, 184, 134, 0.45)" />
              <text
                x={annotationLayout.medianLabelX}
                y={margin.top + annotationLayout.medianLabelY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="af-chart-axis"
                fill="rgba(8, 127, 91, 0.96)"
                style={{ fontWeight: 700 }}
              >
                Median
              </text>
            </g>
          ) : null}

          {mode === "cmf" ? (
            <g>
              <path d={areaD} fill={`url(#${gradId})`} />
              <path d={pathD} className="af-chart-line" />
              {hoverIdx != null ? <circle cx={xForIndex(hoverIdx)} cy={yForValue(activeSeries[hoverIdx])} r="5.2" className="af-chart-dot" /> : null}
            </g>
          ) : (
            probs.map((probability, idx) => {
              const x = margin.left + idx * barW
              const y = yForValue(probability)
              const h = margin.top + innerH - y
              const isHover = idx === hoverIdx
              return <rect key={idx} x={x} y={y} width={Math.max(1, barW - 1)} height={h} fill={`url(#${gradId})`} opacity={isHover ? 1 : 0.92} />
            })
          )}

          {xTicks.map((idx) => {
            const x = xForIndex(idx)
            const value = buckets[idx]?.label ?? ""
            return (
              <g key={idx}>
                <line x1={x} y1={margin.top + innerH} x2={x} y2={margin.top + innerH + 6} className="af-chart-grid-soft" />
                <text x={x} y={computedHeight - 10} textAnchor="middle" className="af-chart-axis-muted">
                  {value}
                </text>
              </g>
            )
          })}
        </svg>
      ) : null}
      <ChartTooltip tooltip={tooltip} containerWidth={width} containerHeight={computedHeight} />
    </div>
  )
}
